import { useEffect, useLayoutEffect, useRef } from 'react';
import Avatar from './Avatar.jsx';
import { dayLabel, timeLabel } from '../lib/format.js';

const TYPING_THROTTLE_MS = 2200;

export default function ChatWindow({
  conv, selfId, online, isFriend, onAddFriend, onSend, onTyping, onClose, onToggleMin,
}) {
  const logRef = useRef(null);
  const inputRef = useRef(null);
  const lastTypingRef = useRef(0);

  useLayoutEffect(() => {
    const log = logRef.current;
    if (log) log.scrollTop = log.scrollHeight;
  }, [conv.messages, conv.typing, conv.minimized]);

  useEffect(() => {
    if (!conv.minimized) inputRef.current?.focus();
  }, [conv.minimized]);

  const submit = (ev) => {
    ev?.preventDefault();
    const input = inputRef.current;
    const body = input.value.trim();
    if (!body) return;
    onSend(conv, body);
    input.value = '';
    input.style.height = 'auto';
  };

  let lastDay = null;

  return (
    <div className={`chat ${conv.minimized ? 'min' : ''} ${conv.minimized && conv.messages.length ? 'unseen' : ''}`}>
      <div
        className="chat-head"
        onClick={(e) => { if (!e.target.closest('[data-action]')) onToggleMin(conv.key); }}
      >
        <Avatar person={conv.peer} size="sm" online={online} />
        <div className="chat-title">
          <div className="chat-name">{conv.peer.displayName}</div>
          {conv.postId ? (
            <div className="chat-context">
              About: {conv.post?.body?.slice(0, 40) || 'a post'}
            </div>
          ) : conv.peer.connectionTopic ? (
            <div className="chat-context">Connected through {conv.peer.connectionTopic}</div>
          ) : null}
          <div className={`chat-status ${online ? 'online' : ''}`}>
            {online
              ? 'on the globe now'
              : conv.peer.userId
                ? 'offline · they’ll see it later'
                : 'gone — guests leave no forwarding address'}
          </div>
        </div>
        {!isFriend && conv.peer.userId && conv.peer.kind !== 'simulation' && (
          <button
            type="button" className="btn btn-sm chat-connect" data-action=""
            onClick={() => onAddFriend?.(conv.peer, conv.post?.tags?.[0] || '', '')}
          >
            Add friend
          </button>
        )}
        <button
          type="button"
          className="icon-btn"
          data-action=""
          aria-label="Close chat"
          onClick={() => onClose(conv.key)}
        >
          ×
        </button>
      </div>

      {!conv.minimized && (
        <>
          <div className="chat-log" ref={logRef}>
            {conv.messages.map((m) => {
              const day = dayLabel(m.createdAt);
              const showDay = day !== lastDay;
              lastDay = day;
              return (
                <Fragmentish key={m.id} showDay={showDay} day={day}>
                  <div className={`bubble ${m.fromId === selfId ? 'me' : 'them'}`}>
                    {(m.quotePostId || (m.postId && conv.post)) && conv.post && (
                      <div className="quoted-post">
                        {conv.post.body || (conv.post.media?.length ? 'Media post' : 'Post')}
                      </div>
                    )}
                    {m.body}
                    <span className="bubble-time">{timeLabel(m.createdAt)}</span>
                  </div>
                </Fragmentish>
              );
            })}
            {conv.messages.length === 0 && (
              <p className="empty-note">Say hello to {conv.peer.displayName}.</p>
            )}
            {conv.typing && <div className="typing">{conv.peer.displayName} is typing…</div>}
          </div>

          <form className="chat-form" onSubmit={submit}>
            <textarea
              ref={inputRef}
              className="chat-input"
              rows={1}
              maxLength={2000}
              placeholder={`Message ${conv.peer.displayName}…`}
              onInput={(e) => {
                e.target.style.height = 'auto';
                e.target.style.height = `${Math.min(96, e.target.scrollHeight)}px`;
                const now = Date.now();
                if (now - lastTypingRef.current > TYPING_THROTTLE_MS) {
                  lastTypingRef.current = now;
                  onTyping(conv);
                }
              }}
              onKeyDown={(e) => {
                if (e.key === 'Enter' && !e.shiftKey) submit(e);
              }}
            />
            <button type="submit" className="send-btn" aria-label="Send">↑</button>
          </form>
        </>
      )}
    </div>
  );
}

function Fragmentish({ showDay, day, children }) {
  return (
    <>
      {showDay && <div className="day-sep">{day}</div>}
      {children}
    </>
  );
}
